import type { Purchase } from '../types/app'

export type ExpiryState = 'active' | 'expiring' | 'expired'

const DAY_MS = 24 * 60 * 60 * 1000

export function remainingLabel(purchase: Pick<Purchase, 'remaining' | 'sessions_total'>): string {
  return `${purchase.remaining} of ${purchase.sessions_total} left`
}

// expiryState reports how a purchase looks today. The server computes `expired`
// authoritatively; this adds the warning band the profile shows, and falls back
// to the date when an older payload has no `expired` flag.
export function expiryState(
  purchase: Pick<Purchase, 'expires_at' | 'expired'>,
  now: Date = new Date(),
): ExpiryState {
  if (!purchase.expires_at) return 'active'
  const expires = new Date(purchase.expires_at).getTime()
  if (purchase.expired || expires <= now.getTime()) return 'expired'
  return expires - now.getTime() <= 30 * DAY_MS ? 'expiring' : 'active'
}

// hasVisibleExpiry reports whether the profile should show an expiry date for
// a purchase. Once every credit has been used the purchase is honoured and
// can no longer expire, so showing a future date next to "0 left" would read
// as though the package were still usable.
export function hasVisibleExpiry(purchase: Pick<Purchase, 'expires_at' | 'remaining'>): boolean {
  return purchase.expires_at !== null && purchase.remaining > 0
}

// isRedeemable reports whether a purchase has a credit Jessica could spend
// right now: still active (not honoured or cancelled), unexpired, and with
// at least one available credit.
export function isRedeemable(
  purchase: Pick<Purchase, 'status' | 'remaining' | 'expires_at' | 'expired'>,
  now: Date = new Date(),
): boolean {
  return purchase.status === 'active' && purchase.remaining > 0 && expiryState(purchase, now) !== 'expired'
}

// carryOverOpen reports whether she can still carry packages over from
// Fresha: until is /v1/me's fresha_carry_over_until (the server allows it
// for FreshaCarryOverDays, 60, after her import is applied).
export function carryOverOpen(until: string | null | undefined, now: Date = new Date()): boolean {
  return !!until && new Date(until).getTime() > now.getTime()
}
